import { createAxiosInstance } from "./index";
import hostConfig from "@/config/hostResolver";

const baseUrl = process.env.VUE_APP_INFORM_URL ?? `${hostConfig.apiBaseUrl}/inform-service`;

/**
 * Give the stored configuration the same key casing the write path uses.
 *
 * The upstream service is ASP.NET, which serialises responses in camelCase by default
 * while binding request bodies case-insensitively. So `POST` with `PascalCase` works and
 * the `GET` comes back `camelCase`, and a reader written against the shape it posts
 * finds nothing — with no error, because a missing key is indistinguishable from an
 * unset one. It cost an empty override dropdown, a field-lock rule that never saw a
 * configuration, and a post-save confirmation that compared undefined against every
 * value it had just written.
 *
 * Normalised here, at the one boundary where the wire format is known, so every caller
 * keeps a single convention rather than each remembering which side it is on.
 *
 * TOP-LEVEL KEYS ONLY. `ExternalPropertyMap` is keyed by the client's own property
 * names — `emp_first_name` and the like — and those are data the merge looks up
 * verbatim. Recasing them would break the rename they exist to drive.
 */
const normaliseEngageConfiguration = (configuration) => {
  if (!configuration || typeof configuration !== "object" || Array.isArray(configuration)) {
    return configuration ?? null;
  }
  return Object.fromEntries(
    Object.entries(configuration).map(([key, value]) => [
      key.charAt(0).toUpperCase() + key.slice(1),
      value,
    ])
  );
};

class InformService {
  constructor() {
    this.apiClient = createAxiosInstance(baseUrl);
  }

  async refreshSchemas({ clientId }) {
    return (
      await this.apiClient.post(baseUrl + "/v3/inform/schema/refresh", undefined, {
        params: {
          clientId,
        },
      })
    )?.data;
  } // refreshSchemas

  async getSchema({ clientId }) {
    return (
      await this.apiClient.get(baseUrl + "/v3/inform/schema", {
        params: {
          clientId,
        },
      })
    )?.data;
  }

  /**
   * Read a client's Engage merge configuration from daas-engage-integration.
   *
   * Goes through the inform-service engage proxy rather than the integration service
   * directly: the proxy authenticates the caller, resolves the client's Inform tenant,
   * mints a tenant-scoped DaaS token, and injects the tenant into the upstream path. So
   * the tenant never appears here, and this SPA holds no DaaS credential.
   *
   * The client travels in `x-target-client-id`, which is what the proxy reads — not a
   * query parameter like the two schema calls above.
   *
   * @param {Object} params
   * @param {number|string} params.clientId
   * @returns {Promise<Object>} The tenant's current configuration document.
   */
  async getEngageConfiguration({ clientId }) {
    const response = await this.apiClient.get(baseUrl + "/v3/engage-proxy/engage/configuration", {
      headers: {
        "x-target-client-id": String(clientId),
      },
    });
    return normaliseEngageConfiguration(response?.data);
  }

  /**
   * Write a client's Engage merge configuration back.
   *
   * Post the WHOLE document. The endpoint replaces rather than patches, so a partial
   * body would blank the labels this tab does not own. Build the body with
   * `buildEngageConfiguration`, which starts from what `getEngageConfiguration`
   * returned for exactly that reason.
   *
   * A configuration write re-merges the entire tenant upstream, so this belongs behind
   * a deliberate save rather than a field watcher.
   *
   * @param {Object} params
   * @param {number|string} params.clientId
   * @param {Object} params.configuration The complete configuration document.
   * @returns {Promise<Object>}
   */
  async saveEngageConfiguration({ clientId, configuration }) {
    return (
      await this.apiClient.post(baseUrl + "/v3/engage-proxy/engage/configuration", configuration, {
        headers: {
          "x-target-client-id": String(clientId),
        },
      })
    )?.data;
  }
}

export default InformService;
